/**
 * ProductServiceAvailabilityPage — Products & Features · Product Service Availability.
 *
 * Per-(product × service) admin-config matrix. Tier-1 foundation (Bill
 * 2026-05-12) — drives the canonical-5 Services cleanup in AddToCartModal
 * (Screen 22 SPEC): DebitCard / OrderChecks / E-Stmts / Internet Banking /
 * Overdraft Protection.
 *
 * Built to match the now-CRUD ProductsPage pattern: reload via config read,
 * DataTable with trailing Edit/Delete ghost buttons, a top "New" Card action, a
 * ModalShell form using FormField primitives, a save() handler, and a confirm
 * delete. The legacy ROP-teams editor
 * (apps/ROP-teams/src/modules/Admin/editors/productServiceAvailabilityEditor.tsx)
 * is the source for the EXACT serviceKey option list + behavior options; per the
 * admin-UI rule all config UI lives in admin-panel, never ROP-teams.
 *
 * Reads:
 *   client.retail.config.productServiceAvailability()  // matrix rows
 *   client.retail.config.products()                    // productKey FK source
 *   client.retail.config.procedureCitations()          // citationKey FK source (REQUIRED)
 *
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key — bearer auth):
 *   client.retail.admin.products.productServiceAvailability.upsert('', body)
 *   client.retail.admin.products.productServiceAvailability.remove('', productKey, serviceKey)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProductServiceAvailabilityUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Matrix rows as returned on the wire by /v3/config/product-service-availability.
// The config read returns Record<string, unknown>; we cast to this local shape.
interface Row {
  productKey: string;
  serviceKey: string;
  behavior: string;
  defaultValue: boolean | null;
  subConfigJson: string | null;
  citationKey: string;
  isActive: boolean;
}

// FK source rows (config reads return Record<string, unknown> — cast locally).
interface ProductRow {
  code: string;
  name?: string | null;
}
interface CitationRow {
  citationKey: string;
  displayLabel?: string | null;
  title?: string | null;
}

// serviceKey option list — copied VERBATIM from the legacy ROP-teams editor
// (productServiceAvailabilityEditor.tsx SERVICES). Do not invent values.
const SERVICE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'DebitCard', label: 'Debit Card (with design picker)' },
  { value: 'OrderChecks', label: 'Order Checks (design + qty)' },
  { value: 'EStmts', label: 'Electronic Statements' },
  { value: 'InternetBanking', label: 'Internet Banking (Banno)' },
  { value: 'OverdraftProtection', label: 'Overdraft Protection (+ Reg E)' },
];

// behavior ENUM — Unavailable | Available | Recommended | Required (REQUIRED).
const BEHAVIOR_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Unavailable', label: 'Unavailable' },
  { value: 'Available', label: 'Available' },
  { value: 'Recommended', label: 'Recommended' },
  { value: 'Required', label: 'Required' },
];

interface EditDraft {
  isNew: boolean;
  productKey: string;
  serviceKey: string;
  behavior: string;
  defaultValue: boolean;
  subConfigJson: string;
  citationKey: string;
  isActive: boolean;
}

function asRow(r: Record<string, unknown>): Row {
  const dv = r.defaultValue;
  return {
    productKey: String(r.productKey ?? ''),
    serviceKey: String(r.serviceKey ?? ''),
    behavior: String(r.behavior ?? ''),
    defaultValue: dv == null ? null : Boolean(dv),
    subConfigJson: r.subConfigJson == null ? null : String(r.subConfigJson),
    citationKey: String(r.citationKey ?? ''),
    isActive: r.isActive == null ? true : Boolean(r.isActive),
  };
}

function rowToDraft(r: Row): EditDraft {
  return {
    isNew: false,
    productKey: r.productKey,
    serviceKey: r.serviceKey,
    behavior: r.behavior || 'Available',
    defaultValue: r.defaultValue ?? false,
    subConfigJson: r.subConfigJson ?? '',
    citationKey: r.citationKey,
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    productKey: '',
    serviceKey: 'DebitCard',
    behavior: 'Available',
    defaultValue: false,
    subConfigJson: '',
    citationKey: '',
    isActive: true,
  };
}

function draftToBody(d: EditDraft): ProductServiceAvailabilityUpsertBody {
  const sub = d.subConfigJson.trim();
  return {
    productKey: d.productKey.trim(),
    serviceKey: d.serviceKey.trim(),
    behavior: d.behavior.trim(),
    defaultValue: d.defaultValue,
    subConfigJson: sub === '' ? null : sub,
    citationKey: d.citationKey.trim(),
    isActive: d.isActive,
  };
}

function behaviorTone(behavior: string): 'ok' | 'warn' | 'muted' | 'info' {
  if (behavior === 'Required') return 'warn';
  if (behavior === 'Recommended') return 'ok';
  if (behavior === 'Unavailable') return 'muted';
  return 'info';
}

export function ProductServiceAvailabilityPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [products, setProducts] = useState<ReadonlyArray<ProductRow>>([]);
  const [citations, setCitations] = useState<ReadonlyArray<CitationRow>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [matrixResp, prodResp, citeResp] = await Promise.all([
        client.retail.config.productServiceAvailability(),
        client.retail.config.products(),
        client.retail.config.procedureCitations(),
      ]);
      if (matrixResp.success && Array.isArray(matrixResp.data)) {
        setRows(matrixResp.data.map(asRow));
      } else {
        setError(matrixResp.error ?? 'Failed to load product service availability.');
        return;
      }
      if (prodResp.success && Array.isArray(prodResp.data)) {
        setProducts(prodResp.data as unknown as ReadonlyArray<ProductRow>);
      } else {
        setProducts([]);
      }
      if (citeResp.success && Array.isArray(citeResp.data)) {
        setCitations(citeResp.data as unknown as ReadonlyArray<CitationRow>);
      } else {
        setCitations([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FK dropdown options for productKey — built from the live products catalog.
  const productOptions = useMemo(
    () => [
      { value: '', label: '— pick a product —' },
      ...products.map((p) => ({
        value: p.code,
        label: `${p.code} — ${p.name ?? ''}`,
      })),
    ],
    [products],
  );

  // FK dropdown options for citationKey — REQUIRED; the server rejects a
  // missing or invalid citation (DO-NOT-GUESS rule). Not free-text.
  const citationOptions = useMemo(
    () => [
      { value: '', label: '— pick a citation —' },
      ...citations.map((c) => ({
        value: c.citationKey,
        label: `${c.citationKey} — ${c.displayLabel ?? c.title ?? ''}`,
      })),
    ],
    [citations],
  );

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.productKey.toLowerCase().includes(n) ||
        r.serviceKey.toLowerCase().includes(n) ||
        r.behavior.toLowerCase().includes(n) ||
        r.citationKey.toLowerCase().includes(n),
    );
  }, [rows, filter]);

  const remove = useCallback(
    async (r: Row): Promise<void> => {
      if (!window.confirm(`Delete service availability ${r.productKey}/${r.serviceKey}?`)) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.productServiceAvailability.remove(
          '',
          r.productKey,
          r.serviceKey,
        );
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      {
        header: 'Product',
        cell: (r) => <code className="cell-mono--bold">{r.productKey}</code>,
      },
      {
        header: 'Service',
        cell: (r) => <code className="cell-mono">{r.serviceKey}</code>,
      },
      {
        header: 'Behavior',
        cell: (r) => <Pill tone={behaviorTone(r.behavior)}>{r.behavior}</Pill>,
      },
      {
        header: 'Default',
        cell: (r) =>
          r.defaultValue == null ? (
            <span className="cell-dim">—</span>
          ) : r.defaultValue ? (
            <Pill tone="ok">on</Pill>
          ) : (
            <Pill tone="muted">off</Pill>
          ),
      },
      {
        header: 'Citation',
        cell: (r) =>
          r.citationKey ? (
            <code className="cell-mono">{r.citationKey}</code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove(r)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [remove],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        if (!d.productKey || !d.serviceKey || !d.citationKey) {
          setSaveErr('Product, service, and citation are required.');
          return;
        }
        if (d.subConfigJson.trim()) {
          try {
            JSON.parse(d.subConfigJson);
          } catch {
            setSaveErr('Sub-config must be valid JSON (or left blank).');
            return;
          }
        }
        const body = draftToBody(d);
        const resp = await client.retail.admin.products.productServiceAvailability.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load product service availability: {error}</Banner>;
  if (!rows) return <Spinner label="Loading product service availability…" />;

  return (
    <>
      <Card
        title={`Product Service Availability — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by product, service, behavior, or citation…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => `${r.productKey}::${r.serviceKey}`}
          emptyState={
            <p className="empty">No service availability rows match the current filter.</p>
          }
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? 'New service availability'
              : `Edit ${draft.productKey}/${draft.serviceKey}`
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Product"
              value={draft.productKey}
              onChange={(v) => setDraft({ ...draft, productKey: v })}
              options={productOptions}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'FK to the Products catalog (primary-key part).'
                  : 'Read-only after create (primary-key part).'
              }
            />
            <SelectField
              label="Service"
              value={draft.serviceKey}
              onChange={(v) => setDraft({ ...draft, serviceKey: v })}
              options={SERVICE_OPTIONS}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'Canonical-5 service (primary-key part).'
                  : 'Read-only after create (primary-key part).'
              }
            />
            <SelectField
              label="Behavior"
              value={draft.behavior}
              onChange={(v) => setDraft({ ...draft, behavior: v })}
              options={BEHAVIOR_OPTIONS}
              required
            />
            <SelectField
              label="Citation"
              value={draft.citationKey}
              onChange={(v) => setDraft({ ...draft, citationKey: v })}
              options={citationOptions}
              required
              hint="FK to Procedure Citations (required — the server rejects a missing or invalid citation)."
            />
            <BoolField
              label="Default value (on / off)"
              value={draft.defaultValue}
              onChange={(v) => setDraft({ ...draft, defaultValue: v })}
              hint="Applies to Recommended / Required services."
            />
            <TextArea
              label="Sub-config JSON"
              value={draft.subConfigJson}
              onChange={(v) => setDraft({ ...draft, subConfigJson: v })}
              rows={3}
              placeholder='optional JSON — DebitCard: {"defaultDesignCode":"105"} · OD: {"defaultLimit":500,"regERequired":true}'
              hint="optional JSON — varies per service; leave blank if none."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
